<template>
	<div ref="viewport" class="virtual-scroller" @scroll.passive="onScroll">
		<div class="virtual-scroller__spacer" :style="{ height: totalHeight + 'px' }"></div>
		<div class="virtual-scroller__view" :style="{ top: offsetY + 'px' }">
			<div
				v-for="entry in visibleEntries"
				:key="entry.key"
				class="virtual-scroller__item"
				:style="{ height: entry.height + 'px' }"
			>
				<slot :item="entry.item" :index="entry.index"></slot>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: "virtualScroller",
	props: {
		items: {
			type: Array,
			default() {
				return [];
			}
		},
		itemHeight: {
			type: Number,
			default: 60
		},
		getItemHeight: {
			type: Function,
			default: null
		},
		itemKey: {
			type: [String, Function],
			default: null
		},
		buffer: {
			type: Number,
			default: 8
		}
	},
	data() {
		return {
			scrollTop: 0,
			viewportHeight: 0
		};
	},
	computed: {
		useFixedHeight() {
			return !this.getItemHeight;
		},
		offsets() {
			if (this.useFixedHeight) {
				return null;
			}
			const items = this.items || [];
			const offsets = new Array(items.length + 1);
			offsets[0] = 0;
			const getter = this.getItemHeight;
			for (let i = 0; i < items.length; i++) {
				offsets[i + 1] = offsets[i] + getter(items[i], i);
			}
			return offsets;
		},
		totalHeight() {
			const items = this.items || [];
			if (this.useFixedHeight) {
				return items.length * this.itemHeight;
			}
			const offsets = this.offsets;
			return offsets[offsets.length - 1] || 0;
		},
		viewHeight() {
			return this.viewportHeight || 600;
		},
		visibleEntries() {
			const items = this.items || [];
			if (!items.length) {
				return [];
			}
			const start = Math.max(0, this.findStartIndex(this.scrollTop) - this.buffer);
			const bottom = this.scrollTop + this.viewHeight;
			let end = start;
			if (this.useFixedHeight) {
				const last = Math.ceil(bottom / this.itemHeight);
				end = Math.min(items.length, last + this.buffer);
			} else {
				const offsets = this.offsets;
				while (end < items.length && offsets[end] < bottom) {
					end++;
				}
				end = Math.min(items.length, end + this.buffer);
			}
			const entries = [];
			for (let i = start; i < end; i++) {
				entries.push({
					item: items[i],
					index: i,
					key: this.resolveKey(items[i], i),
					height: this.useFixedHeight ? this.itemHeight : (this.offsets[i + 1] - this.offsets[i])
				});
			}
			return entries;
		},
		offsetY() {
			if (!this.visibleEntries.length) {
				return 0;
			}
			const index = this.visibleEntries[0].index;
			if (this.useFixedHeight) {
				return index * this.itemHeight;
			}
			return this.offsets[index] || 0;
		}
	},
	watch: {
		items() {
			this.$nextTick(() => {
				this.measure();
				this.clampScroll();
			});
		},
		totalHeight() {
			this.clampScroll();
		}
	},
	mounted() {
		this.measure();
		this.$nextTick(this.measure);
		window.addEventListener('resize', this.measure);
		if (typeof ResizeObserver !== 'undefined') {
			this.resizeObserver = new ResizeObserver(() => this.measure());
			if (this.$refs.viewport) {
				this.resizeObserver.observe(this.$refs.viewport);
			}
		}
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.measure);
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
			this.resizeObserver = null;
		}
	},
	methods: {
		init() {
			this.scrollToTop();
		},
		scrollToTop() {
			if (this.$refs.viewport) {
				this.$refs.viewport.scrollTop = 0;
			}
			this.scrollTop = 0;
		},
		measure() {
			if (this.$refs.viewport) {
				this.viewportHeight = this.$refs.viewport.clientHeight || 0;
			}
		},
		clampScroll() {
			const maxScroll = Math.max(0, this.totalHeight - this.viewHeight);
			if (this.scrollTop > maxScroll) {
				this.scrollTop = maxScroll;
				if (this.$refs.viewport) {
					this.$refs.viewport.scrollTop = maxScroll;
				}
			}
		},
		onScroll(e) {
			if (!this.viewportHeight) {
				this.measure();
			}
			this.scrollTop = e.target.scrollTop;
		},
		findStartIndex(scrollTop) {
			const items = this.items || [];
			if (this.useFixedHeight) {
				if (!items.length) {
					return 0;
				}
				return Math.min(items.length - 1, Math.max(0, Math.floor(scrollTop / this.itemHeight)));
			}
			const offsets = this.offsets;
			let lo = 0;
			let hi = Math.max(0, offsets.length - 2);
			while (lo <= hi) {
				const mid = (lo + hi) >> 1;
				if (offsets[mid] <= scrollTop) {
					if (offsets[mid + 1] > scrollTop) {
						return mid;
					}
					lo = mid + 1;
				} else {
					hi = mid - 1;
				}
			}
			return 0;
		},
		resolveKey(item, idx) {
			if (typeof this.itemKey === 'function') {
				return this.itemKey(item, idx);
			}
			if (this.itemKey && item) {
				return item[this.itemKey];
			}
			if (item && item.key != null) {
				return item.key;
			}
			if (item && item.id != null) {
				return item.id;
			}
			return idx;
		}
	}
};
</script>

<style scoped>
.virtual-scroller {
	position: relative;
	height: 100%;
	overflow-y: auto;
	overflow-x: hidden;
}

.virtual-scroller__spacer {
	width: 1px;
	pointer-events: none;
}

.virtual-scroller__view {
	position: absolute;
	left: 0;
	right: 0;
}

.virtual-scroller__item {
	box-sizing: border-box;
}
</style>
